import { castArray } from 'lodash-es';
import { find, map, pathOr, pick, pipe } from 'remeda';
import invariant from 'tiny-invariant';

import {
    Element,
    ELEMENT_TYPES,
    type InfoboxContentElement,
    type InfoboxElement,
    type ParagraphElement,
} from '@@editor/helpers/Element';
import { isUnityInfobox, type UnityInfobox } from '@@editor/typings/UnityElements';
import addDefaultElementIfEmptyState from '@@editor/utils/addDefaultElementIfEmptyState';
import makeDefaultTextIfNull from '@@editor/utils/makeDefaultUnityTextIfNull';
import toParagraph from '@@editor/utils/toParagraph';

import { CURRENT_VERSION } from '../../constants';
import { type ElementRule, type Next } from '../../types';
import { createElementFromSingleLineState, createElementFromState } from './../../deserializeNodes';

const deserializeTitle = (next: Next<ParagraphElement>) => (title: UnityInfobox['title']) =>
    pipe(
        title,
        makeDefaultTextIfNull,
        next,
        castArray,
        createElementFromSingleLineState(ELEMENT_TYPES.INFOBOX_TITLE),
    );

const deserializeContent =
    (next: Next<InfoboxContentElement['children'][number]>) => (content: UnityInfobox['content']) =>
        pipe(
            content,
            addDefaultElementIfEmptyState,
            map(next),
            createElementFromState(ELEMENT_TYPES.INFOBOX_CONTENT),
        );

const serializeTitle =
    (next: Next<UnityInfobox['title']>) => (children: InfoboxElement['children']) =>
        pipe(children, find(Element.isInfoboxTitleElement), toParagraph, next);

const serializeContent =
    (next: Next<UnityInfobox['content']>) => (children: InfoboxElement['children']) =>
        pipe(
            children,
            find(Element.isInfoboxContentElement),
            pathOr(['children'] as const, []),
            map(next),
        );

type Rule = ElementRule<InfoboxElement, UnityInfobox>;

const infoboxRules: Rule = {
    deserialize: (element, next): ReturnType<Rule['deserialize']> => {
        if (isUnityInfobox(element)) {
            invariant(next, 'Cannot deserialize Infobox without a `next` function');

            const { content, title, type } = element;

            return {
                type,
                data: pick(element, ['collapsed', 'style', 'templateElement']),
                children: [deserializeTitle(next)(title), deserializeContent(next)(content)],
            };
        }
    },

    serialize: (node, next): ReturnType<Rule['serialize']> => {
        if (Element.isInfoboxElement(node)) {
            invariant(next, 'Cannot serialize Infobox without a `next` function');

            const { children, data, type } = node;

            return {
                ...pick(data, ['collapsed', 'style', 'templateElement']),
                version: CURRENT_VERSION,
                type,
                title: serializeTitle(next)(children),
                content: serializeContent(next)(children),
            };
        }
    },
};

export default infoboxRules;
